import React, { useState } from 'react';
import { QueryResult, ApolloQueryResult } from '@apollo/client';
import { useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import Form, { Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Button, { LoadingButton } from '@atlaskit/button';
import {
  AddressFormSection,
  SelectShipToAddress,
  useFormSubmit,
  useNotification,
  ErrorComponents,
  InfoPanel,
  useSidebar,
  successNotificationBuilder,
  shipToAddressesType,
  EditCreateInvoiceGroup,
} from '@css-xp/base';
import { Checkbox } from '@atlaskit/checkbox';
import Textfield from '@atlaskit/textfield';
import { CommerceAddress, Currency, FetchShipToPartiesForTransactionAccountQuery } from '@css-xp/graphtypes';
import ButtonGroup from '@atlaskit/button/button-group';
import { createInvoiceGroup } from '@atlassian/temp-proxies';
import { CreateInvoiceGroupRequest, InvoiceGroupWarnings } from '@atlassian/cssxp-types-billing';
import { fetchShippingAddressesForTransactionAccount } from '../../../tabs/invoice-group-tabs';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
// import { ENABLE_PO_NUMBER_INVOICE_GROUP } from '@css-xp/types-featureflag';
// import { getFeatureFlag } from '../common/security/selectors';
// import { useSelector } from 'react-redux';

interface FormData {
  shipToName: string;
  shipToAddress1?: string | null;
  shipToAddress2?: string | null;
  shipToCity?: string | null;
  shipToCountry?: { value: string; label: string };
  shipToPostalCode?: string | null;
  shipToState?: { value: string; label: string };
  shipToTaxId?: string | null;
  currency: string;
  recipients: string[];
  invoiceGroupName: string;
  purchaseOrderNumber?: string | null;
}

export const CreateInvoiceGroupSidebar = ({
  transactionAccountId,
  billToAddress,
  refetch,
}: {
  transactionAccountId: string;
  billToAddress: CommerceAddress;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
}) => {
  const dispatch = useDispatch();
  const [adminFieldsDirty, setAdminsDirtyFlag] = useState(true);

  const [isChecked, setIsChecked] = useState(false);
  const [showPOCheckbox, setShowPOCheckbox] = useState(false);
  const [isPOSingleUseChecked, setIsPOSingleUseChecked] = useState(true);
  const [shipToAddress, setShipToAddress] = useState<CommerceAddress | undefined | null>({});

  const { closeSidebar } = useSidebar();
  const [shipToAddresses, setShipToAddresses] = React.useState<
    Partial<QueryResult<FetchShipToPartiesForTransactionAccountQuery>>
  >({ loading: true });

  React.useEffect(() => {
    (async () => {
      const shipToAddresses = await graphqlClient().query({
        query: fetchShippingAddressesForTransactionAccount,
        variables: { id: transactionAccountId },
        fetchPolicy: 'no-cache',
      });

      setShipToAddresses({ ...shipToAddresses });
    })();
  }, []);
  const isPONumberFeatureEnabled = true;

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  const onSelect = (selected: any) => {
    setShipToAddress(selected?.value);
  };

  const { error, isProcessing, submitForm, complete, data } = useFormSubmit<CreateInvoiceGroupRequest, any>({
    submitFormAction: createInvoiceGroup,
  });

  const submitCreateInvoiceGroupForm = (formData: FormData) => {
    const requestForm: CreateInvoiceGroupRequest = {
      transactionAccountId: transactionAccountId,
      data: {
        name: formData.invoiceGroupName,
        // shipToPartyId: shipToAddress?.id,
        shipToParty: {
          name: formData.shipToName,
          taxId: formData.shipToTaxId === '' ? null : formData.shipToTaxId,
          postalAddress: {
            line1: formData.shipToAddress1,
            line2: formData.shipToAddress2 === '' ? null : formData.shipToAddress2,
            city: formData.shipToCity,
            country: formData.shipToCountry?.value,
            state: formData.shipToState?.value,
            postcode: formData.shipToPostalCode,
          },
        },
        currency: formData.currency,
        recipients: (formData.recipients ?? []).filter((recipient: string) => recipient.length > 0),
      },
    };

    const existingShipTo = {
      shipToParty: {
        name: shipToAddress?.name,
        taxId: shipToAddress?.taxId,
        postalAddress: {
          line1: shipToAddress?.addressLine1,
          line2: shipToAddress?.addressLine2,
          city: shipToAddress?.city,
          country: shipToAddress?.country,
          state: shipToAddress?.state,
          postcode: shipToAddress?.postcode,
        },
      },
    };

    // check if we selected an existing shipTo and not made any updates to it
    if (!isEmpty(shipToAddress) && isEqual(existingShipTo.shipToParty, requestForm.data.shipToParty)) {
      // send existing shipToPartyId to update it
      requestForm.data.shipToPartyId = shipToAddress?.id;
    }

    if (isPONumberFeatureEnabled && formData.purchaseOrderNumber) {
      requestForm.data.purchaseOrder = {
        number: formData.purchaseOrderNumber,
        oneTimeUse: isPOSingleUseChecked,
      };
    }
    submitForm(requestForm);
  };

  if (complete && !notificationSent) {
    const successMessage = successNotificationBuilder()
      .withId('create-invoice-group-success')
      .withHeader(`Successfully created invoice group`)
      .withMessage(`created invoice group: ${data?.id}`)
      .build();
    sendSuccessNotification(successMessage);
    setNotificationSent(true);
    closeSidebar();
    if (refetch) {
      refetch();
    }
  }
  const updateAdminFieldsDirtyFlag = (flag: boolean) => {
    setAdminsDirtyFlag(flag);
  };
  /* istanbul ignore next */
  const onSameAsBillTo = () => {
    // set the shipTo equal to our bill to address
    setShipToAddress(isChecked ? {} : billToAddress);
    setIsChecked((current) => !current);
  };
  /* istanbul ignore next */
  const onPOSingleUseChangeEvent = () => {
    setIsPOSingleUseChecked((current) => !current);
  };
  const onPOTextChange = (value: string) => {
    if (value !== '') {
      setShowPOCheckbox(true);
    } else {
      setShowPOCheckbox(false);
    }
  };
  // const isPONumberFeatureEnabled = useSelector(getFeatureFlag(ENABLE_PO_NUMBER_INVOICE_GROUP));

  return (
    <div style={{ maxWidth: '600px', padding: '25px' }}>
      <Form onSubmit={submitCreateInvoiceGroupForm}>
        {({ formProps, submitting, setFieldValue, dirty }) => (
          <form {...formProps}>
            <FormHeader title="Create Invoice Group"></FormHeader>
            <FormSection>
              {error && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to create a new invoice group for the following reason"
                      message={error}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              <div style={{ maxWidth: '500px' }}>
                <h3>Invoice group name</h3>

                <Field name="invoiceGroupName" id="invoiceGroupName" defaultValue={''} label="Name" isRequired={true}>
                  {({ fieldProps }) => (
                    <Textfield {...fieldProps} name="invoiceGroupName" testId="invoice-group-name" />
                  )}
                </Field>
              </div>
            </FormSection>
            <FormSection>
              <div style={{ maxWidth: '500px' }}>
                <h3 style={{ marginBottom: '10px' }}>Ship to address</h3>
                <Checkbox
                  isChecked={isChecked}
                  onChange={onSameAsBillTo}
                  label="Same as bill to address"
                  value="Controlled Checkbox"
                  name="controlled-checkbox"
                  data-testid="same-as-bill"
                />
                <SelectShipToAddress
                  // @ts-ignore -- Complaining about transactionAccountId type
                  transactionAccountId={transactionAccountId}
                  shipToAddresses={shipToAddresses?.data as shipToAddressesType}
                  onSelect={onSelect}
                />
                <AddressFormSection
                  {...shipToAddress}
                  isEditable={true}
                  addressName="Ship to"
                  setFieldValue={setFieldValue}
                  isRequired={true}
                />
              </div>
            </FormSection>
            <FormSection>
              <div style={{ maxWidth: '500px' }} data-testId="Invoice contacts">
                <EditCreateInvoiceGroup
                  recipients={['']}
                  updateAdminFieldsDirtyFlag={updateAdminFieldsDirtyFlag}
                  isRequired={false}
                  placeholder={'Email address'}
                  helperMessage={'Add/remove or update the invoice contacts here'}
                  title={'Invoice contacts'}
                />
              </div>
            </FormSection>
            <FormSection>
              <div style={{ maxWidth: '500px' }}>
                <h3>Currency</h3>
                <Field name="currency" isRequired id="currency" defaultValue={Currency.USD} isDisabled={true}>
                  {({ fieldProps }) => <Textfield {...fieldProps} name="currency" testId="currency-usd" />}
                </Field>
              </div>
            </FormSection>
            <FormSection>
              {isPONumberFeatureEnabled && (
                <div style={{ maxWidth: '500px' }}>
                  <h3>Purchase order</h3>
                  <Field name="purchaseOrderNumber" id="purchaseOrderNumber" label="Number" defaultValue={''}>
                    {({ fieldProps }) => (
                      <Textfield
                        {...fieldProps}
                        name="purchaseOrderNumber"
                        testId="purchase-order-number"
                        onChange={(event) => {
                          onPOTextChange(event.currentTarget.value);
                          fieldProps.onChange(event.currentTarget.value);
                        }}
                      />
                    )}
                  </Field>
                  <br />
                  {showPOCheckbox && (
                    <>
                      <Checkbox
                        isChecked={isPOSingleUseChecked}
                        onChange={onPOSingleUseChangeEvent}
                        label="Multiple Use PO Number"
                        value="Multiple Use PO Number"
                        name="multipleUse"
                        testId="po-multiple-use"
                      />
                      <InfoPanel type="warning" message={InvoiceGroupWarnings.PO_MULTIPLE_USE_WARNING} />
                    </>
                  )}
                </div>
              )}
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={submitting || isProcessing}
                  isDisabled={!dirty && !adminFieldsDirty}
                  testId={'create-button'}
                >
                  Create
                </LoadingButton>
                <Button appearance="subtle" onClick={closeSidebar} testId="cancel">
                  Close
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
